import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const ThemeContext = createContext(null);

const STORAGE_KEY = "atech-theme";

export function ThemeProvider({ children, defaultTheme = "dark" }) {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === "undefined") return defaultTheme;
    try { return window.localStorage.getItem(STORAGE_KEY) || defaultTheme; } catch (_e) { return defaultTheme; }
  });

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    if (theme === "light") {
      root.classList.add("theme-light");
      body.classList.add("theme-light");
    } else {
      root.classList.remove("theme-light");
      body.classList.remove("theme-light");
    }
    try { window.localStorage.setItem(STORAGE_KEY, theme); } catch (_e) { /* SSR / private-mode guard */ }
  }, [theme]);

  const setTheme = useCallback((t) => setThemeState(t === "light" ? "light" : "dark"), []);
  const toggle = useCallback(() => setThemeState((t) => (t === "light" ? "dark" : "light")), []);

  const value = useMemo(() => ({ theme, setTheme, toggle }), [theme, setTheme, toggle]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
};

/**
 * Read the saved user preference, falling back to the server-configured default.
 * Call once when settings are loaded to sync the initial theme.
 */
export function resolveInitialTheme(serverDefault = "dark") {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "dark" || stored === "light") return stored;
  } catch (_e) { /* SSR / private-mode guard */ }
  return serverDefault === "light" ? "light" : "dark";
}
